import type { WebSocketEvent } from '@cade/shared-types';

type EventHandler = (event: WebSocketEvent) => void;

class WebSocketManager {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, EventHandler[]>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectHandlers = new Set<() => void>();
  private hasConnectedBefore = false;
  private url: string;
  private _lastUserId = '';
  private _lastTenantId = '';
  private _lastToken = '';

  constructor() {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    // In production, WebSocket goes directly to ALB via api.getcade.ai (CloudFront doesn't support WS).
    const wsHost = location.hostname === 'localhost'
      ? `${location.hostname}:3003`
      : location.hostname.replace('app.', 'api.');
    this.url = `${proto}//${wsHost}/ws`;
  }

  connect(userId: string, tenantId: string, token: string): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) return;

    this._lastUserId = userId;
    this._lastTenantId = tenantId;
    this._lastToken = token;

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.ws?.send(JSON.stringify({ type: 'auth', token, userId, tenantId }));
      if (this.hasConnectedBefore) {
        this.reconnectHandlers.forEach((h) => h());
      }
      this.hasConnectedBefore = true;
    };

    this.ws.onmessage = (e: MessageEvent<string>) => {
      try {
        const data = JSON.parse(e.data) as WebSocketEvent;
        if ('conversationId' in data) {
          const convHandlers = this.handlers.get(data.conversationId) ?? [];
          convHandlers.forEach((h) => h(data));
          const globalHandlers = this.handlers.get('*') ?? [];
          globalHandlers.forEach((h) => h(data));
        }
      } catch {
        // ignore parse errors
      }
    };

    this.ws.onclose = () => {
      this.reconnectTimer = setTimeout(
        () => this.connect(this._lastUserId, this._lastTenantId, this._lastToken),
        3000,
      );
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  subscribe(conversationId: string, handler: EventHandler): () => void {
    const existing = this.handlers.get(conversationId) ?? [];
    this.handlers.set(conversationId, [...existing, handler]);
    return () => {
      const updated = (this.handlers.get(conversationId) ?? []).filter((h) => h !== handler);
      this.handlers.set(conversationId, updated);
    };
  }

  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => { this.reconnectHandlers.delete(handler); };
  }

  send(data: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  disconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }
}

export const wsManager = new WebSocketManager();
